import { css } from "@emotion/css";
import { SimpleDataTable } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { Repository, ProjectTemplateVersionResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
import Chip from "~/components/Chips/Chip";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import { PageContent } from "~/components/PageContent/PageContent";
import DateFormatter from "~/utils/DateFormatter/index";
interface ProjectTemplateVersionsPageProps {
    loaderData: LoaderData;
}
interface LoaderData {
    publishedVersions: ProjectTemplateVersionResource[];
}
export async function projectTemplateVersionsPageLoader(repository: Repository, { routeParams }: {
    routeParams: {
        slug: string;
        gitRef: string;
    };
}): Promise<LoaderData> {
    const versionsResponse = await repository.ProjectTemplates.getVersions(routeParams.slug);
    return {
        publishedVersions: versionsResponse.ProjectTemplateVersions,
    };
}
export function ProjectTemplateVersionsPage({ loaderData }: ProjectTemplateVersionsPageProps) {
    return (<PageContent header={{
            title: "Project Template Versions",
        }}>
            <ProjectTemplateVersions publishedVersions={loaderData.publishedVersions}/>
        </PageContent>);
}
export function ProjectTemplateVersions({ publishedVersions }: {
    publishedVersions: ProjectTemplateVersionResource[] | null;
}) {
    if (publishedVersions === null) {
        return null;
    }
    if (publishedVersions.length === 0) {
        return (<OnboardingPage title="No versions found for this template" intro="Create versions of project templates on any branch to test and publish to the rest of your team. To get started on publishing a version, select 'Publish' in the top right corner." learnMore={null}/>);
    }
    return (<div>
            <SimpleDataTable columns={[
            {
                title: "Version",
                render: (data) => (<div className={styles.versionContainer}>
                                <b>{data.Version}</b>
                                {data.IsPreRelease ? <Chip backgroundColor={themeTokens.color.chip.filled.background.info}>Pre-release</Chip> : null}
                            </div>),
                columnSize: "medium",
            },
            { title: "Git reference", render: (data) => <GitRefChip vcsRef={{ GitRef: data.GitRef, GitCommit: data.GitCommit }} showCommit={true}/>, columnSize: "medium" },
            { title: "Published", render: (data) => DateFormatter.dateToLongFormat(data.PublishedDate) },
        ]} data={publishedVersions} getRowKey={(data) => data.Version}/>
        </div>);
}
const styles = {
    versionContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
    }),
    stopSharingMenuItem: css({
        color: themeTokens.color.text.danger,
    }),
    pageHeaderContainer: css({
        display: "flex",
        flexDirection: "column",
        paddingBottom: space[32],
        gap: "unset",
    }),
    breadcrumbContainer: css({
        display: "flex",
        alignItems: "flex-start",
        height: "1.75rem",
    }),
    titleContainer: css({
        flex: "1",
        display: "flex",
        gap: space[12],
        alignItems: "center",
        overflow: "hidden",
    }),
    title: css({
        all: "unset",
        font: text.heading.large,
        color: themeTokens.color.text.primary,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    }),
    commitButtonContainerStyles: css({
        display: "inline-flex",
        gap: space[4],
    }),
};
